import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { addBackgroundToAudio, checkTaskStatus, getDefaultAudioUrl } from '../../services/audioApi';
import { 
    Upload, Download, Loader2, AlertCircle, 
    CheckCircle, XCircle, Mic, Music, Volume2 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';


const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €"; 

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};


const AudioBackgroundTool = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    
    // File State
    const [mainFile, setMainFile] = useState(null);
    const [bgFile, setBgFile] = useState(null);
    const [bgMode, setBgMode] = useState('default'); // 'default' or 'custom'
    
    // Previews
    const [previewMain, setPreviewMain] = useState(null);
    const [previewDefaultBg, setPreviewDefaultBg] = useState(null);
    
    // Params (0 to 40, matching streamlit logic. 40 is quieter background)
    const [volumeReduction, setVolumeReduction] = useState(10);
    const [error, setError] = useState(null);
    
    // Process State
    const [isProcessing, setIsProcessing] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [progressValue, setProgressValue] = useState(0);
    const [resultUrl, setResultUrl] = useState(null);
    
    const pollingIntervalRef = useRef(null);

    // Fetch default audio preview on mount
    useEffect(() => {
        const fetchDefault = async () => {
            try {
                const data = await getDefaultAudioUrl();
                setPreviewDefaultBg(data.url);
            } catch (err) {
                console.error("Default audio fetch failed", err);
            }
        };
        fetchDefault();
    }, []);

    // Cleanup object URLs
    useEffect(() => {
        return () => {
            if (previewMain) URL.revokeObjectURL(previewMain);
        };
    }, [previewMain]);

    // Cleanup polling
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, []);

    // Polling Logic
    useEffect(() => {
        if (taskId && isProcessing) {
            pollingIntervalRef.current = setInterval(async () => {
                try {
                    const data = await checkTaskStatus(taskId);
                    
                    if (data.state === 'SUCCESS' || data.status === 'completed') {
                        clearInterval(pollingIntervalRef.current);
                        const payload = data.result;
			const innerResult = payload.result;

                        if (innerResult?.new_credits !== undefined) {
                            setCredits(innerResult.new_credits);
                        }
                        if (payload.status === 'completed' && innerResult?.download_url) {
                            setResultUrl(innerResult.download_url);
                            setProgressValue(100);
                            setIsProcessing(false);
                        } else {
                            setError(t('audioBackground.errors.generic'));
                            setIsProcessing(false);
                        }
                    } else if (data.state === 'FAILURE') {
                        clearInterval(pollingIntervalRef.current);
                        setError(data.error || t('audioBackground.errors.generic'));
                        setIsProcessing(false);
                    } else {
                        // Progress update
                        if (data.info && data.info.progress) {
                            setProgressValue(data.info.progress);
                        }
                    }
                } catch (err) {
                    clearInterval(pollingIntervalRef.current);
                    setError(err.message);
                    setIsProcessing(false);
                }
            }, 2000);
        }
        return () => {
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        };
    }, [taskId, isProcessing, t]);

    const handleMainFile = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 25 * 1024 * 1024) {
                setError(t('audioBackground.errors.tooLarge'));
                return;
            }
            setMainFile(file);
            setPreviewMain(URL.createObjectURL(file));
            setError(null);
            setResultUrl(null);
        }
    };

    const handleProcess = async () => {
        if (!mainFile) {
            setError(t('audioBackground.errors.missingMain'));
            return;
        }
        if (bgMode === 'custom' && !bgFile) {
            setError(t('audioBackground.errors.missingBg'));
            return;
        }

        setIsProcessing(true);
        setError(null);
        setProgressValue(5);

        try {
            const response = await addBackgroundToAudio(
                mainFile,
                bgMode === 'custom' ? bgFile : null,
                volumeReduction
            );
            setTaskId(response.task_id);
        } catch (err) {
            setError(err.message);
            setIsProcessing(false);
        }
    };

    const handleClear = () => {
        setMainFile(null);
        setBgFile(null);
        setResultUrl(null);
        if (previewMain) URL.revokeObjectURL(previewMain);
        setPreviewMain(null);
        setTaskId(null);
    };

    const UploadView = () => (
        <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-2 text-brand-orange">{t('audioBackground.title')}</h2>
            <p className="mt-2 text-brand-dark">{t('audioBackground.description')}</p>

            <div className="mt-8 space-y-6">
                
                {/* 1. Main Audio */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                    <label className="block text-lg font-medium mb-3 text-brand-dark flex items-center">
                        <Mic className="mr-2" size={20} /> {t('audioBackground.mainAudioLabel')}
                    </label>
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                        <label className="flex-1 w-full flex flex-col items-center p-6 border-2 border-dashed border-slate-300 rounded-md bg-slate-50 cursor-pointer hover:bg-slate-100 transition">
                            <Upload className="h-8 w-8 text-slate-400 mb-2" />
                            <span className="text-sm text-brand-orange font-semibold">
                                {mainFile ? mainFile.name : t('audioBackground.selectFile')}
                            </span>
                            <span className="text-xs text-slate-500 mt-1">{t('audioBackground.limit')}</span>
                            <input type="file" className="sr-only" onChange={handleMainFile} accept="audio/*" />
                        </label>
                        {previewMain && (
                            <div className="w-full md:w-1/3">
                                <audio controls src={previewMain} className="w-full h-10" />
                            </div>
                        )}
                    </div>
                </div>

                {/* 2. Background Source */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                    <label className="block text-lg font-medium mb-3 text-brand-dark flex items-center">
                        <Music className="mr-2" size={20} /> {t('audioBackground.bgAudioLabel')}
                    </label>

                    <div className="flex gap-4 mb-4">
                        <button 
                            onClick={() => setBgMode('default')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition ${bgMode === 'default' ? 'bg-brand-orange text-white' : 'bg-slate-100 text-slate-600'}`}
                        >
                            {t('audioBackground.useDefault')}
                        </button>
                        <button 
                            onClick={() => setBgMode('custom')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition ${bgMode === 'custom' ? 'bg-brand-orange text-white' : 'bg-slate-100 text-slate-600'}`}
                        >
                            {t('audioBackground.uploadOwn')}
                        </button>
                    </div>

                    {bgMode === 'default' ? (
                        <div className="bg-slate-50 p-4 rounded border border-slate-200">
                            <p className="text-sm text-slate-600 mb-2">{t('audioBackground.defaultInfo')}</p>
                            {previewDefaultBg && <audio controls src={previewDefaultBg} className="w-full h-8" />}
                        </div>
                    ) : (
                        <div className="flex items-center p-4 border-2 border-dashed border-slate-300 rounded-md bg-slate-50">
                            <label className="cursor-pointer flex items-center w-full">
                                <span className="text-sm text-brand-orange font-semibold mr-2">{t('audioBackground.uploadBg')}</span>
                                <span className="text-slate-500 text-sm truncate">{bgFile ? bgFile.name : ''}</span>
                                <input type="file" className="sr-only" onChange={(e) => setBgFile(e.target.files[0])} accept="audio/*" />
                            </label>
                            {bgFile && (
                                <button onClick={() => setBgFile(null)}><XCircle className="text-red-500" size={18} /></button>
                            )}
                        </div>
                    )}
                </div>

                {/* 3. Volume Adjustment */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                    <label className="block text-lg font-medium mb-3 text-brand-dark flex items-center">
                        <Volume2 className="mr-2" size={20} /> {t('audioBackground.volumeLabel')}
                    </label>
                    <p className="text-sm text-slate-500 mb-4">{t('audioBackground.volumeHint')}</p>
                    
                    <div className="flex items-center gap-4">
                        <span className="text-xs font-bold text-slate-400">{t('audioBackground.volHigh')}</span>
                        <input 
                            type="range" 
                            min="0" 
                            max="40" 
                            step="1"
                            // Reverse logic for UI: Slider right (High value) means low reduction (0). Slider left means high reduction (40).
                            value={40 - volumeReduction} 
                            onChange={(e) => setVolumeReduction(40 - parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-brand-orange"
                        />
                        <span className="text-xs font-bold text-slate-400">{t('audioBackground.volLow')}</span>
                    </div>
                </div>

                {error && <p className="text-red-500 flex items-center"><AlertCircle size={16} className="mr-2"/>{error}</p>}

                <button
                    onClick={handleProcess}
                    disabled={!mainFile || isProcessing}
                    className="w-full px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    {isProcessing ? <><Loader2 className="animate-spin mr-2" /> {t('audioBackground.processing')}</> : t('audioBackground.processButton')}
                </button>

                {isProcessing && (
                    <div className="mt-4">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>{t('audioBackground.progress')}</span>
                            <span>{progressValue}%</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                            <div className="bg-brand-orange h-2 rounded-full transition-all duration-500" style={{ width: `${progressValue}%` }}></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const ResultView = () => (
        <div className="max-w-2xl mx-auto text-center mt-10">
            <div className="bg-green-50 border border-green-200 p-6 rounded-lg mb-8">
                <div className="flex justify-center mb-4">
                    <CheckCircle className="text-green-500 h-12 w-12" />
                </div>
                <h3 className="text-xl font-bold text-green-800 mb-2">{t('audioBackground.success')}</h3>
                
                <div className="bg-white p-4 rounded-md shadow-sm mt-4">
                    <audio controls src={resultUrl} className="w-full" />
                </div>
                
                <a 
                    href={resultUrl} 
                    download="mixed_audio.mp3"
                    className="mt-6 inline-flex items-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition"
                >
                    <Download className="mr-2" size={18} /> {t('audioBackground.download')}
                </a>
            </div>
            
            <button onClick={handleClear} className="text-slate-600 hover:text-brand-orange underline">
                {t('audioBackground.processAnother')}
            </button>
        </div>
    );

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? <SubscribePrompt />  : resultUrl ? <ResultView /> : <UploadView />}
        </div>
    );
};

export default AudioBackgroundTool;
